import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AdCard from "../components/AdCard";
import CPSearch from "../components/CPSearch";
import { api } from "../lib/api";
import { MapPin, Info, Filter, Search, X, TrendingUp } from "lucide-react";

export default function Directory() {
  const { cp } = useParams();
  const [params, setParams] = useSearchParams();
  const giro = params.get("giro") || "";
  const initialQ = params.get("q") || "";
  const [qInput, setQInput] = useState(initialQ);
  const [q, setQ] = useState(initialQ);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Debounce typing into the search query
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Fetch directory data (cancellable to prevent stale responses)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const search = new URLSearchParams();
    search.set("postal_code", cp);
    if (giro) search.set("giro", giro);
    if (q) search.set("q", q);
    api.get(`/directory/search?${search.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ results: [], giros_available: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cp, giro, q]);

  // Sync `q` to URL (separately, only when it actually changes, no replace storm)
  useEffect(() => {
    const currentQ = params.get("q") || "";
    if (q === currentQ) return;
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const setGiro = (g) => {
    const next = new URLSearchParams(params);
    if (g) next.set("giro", g);
    else next.delete("giro");
    if (q) next.set("q", q);
    setParams(next, { replace: true });
  };

  const giros = data?.giros_available || [];
  const results = data?.results || [];
  const hasFilters = !!(giro || q);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero with CP + Search */}
      <section className="max-w-7xl mx-auto px-6 pt-10 pb-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="max-w-3xl">
            <div className="overline text-[hsl(var(--primary))]">Directorio local · CP {data?.matched_postal_code || cp}</div>
            {data?.directory_name ? (
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2 leading-[1.05]" data-testid="directory-title">
                Bienvenido a <span className="bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] bg-clip-text text-transparent">{data.directory_name}</span>
              </h1>
            ) : (
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3" data-testid="directory-title">
                <MapPin className="w-8 h-8 text-[#7C3AED]" />
                Directorio CP {data?.matched_postal_code || cp}
              </h1>
            )}
            {data && !data.is_exact_match && data.matched_postal_code && (
              <div className="mt-3 flex items-start gap-2 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg px-4 py-2 text-sm" data-testid="nearest-cp-banner">
                <Info className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))]" />
                <span>
                  Aún no tenemos directorio en CP <strong>{cp}</strong>. Te mostramos el más cercano:{" "}
                  <strong>CP {data.matched_postal_code}</strong>{data.directory_name ? ` (${data.directory_name})` : ""}.
                </span>
              </div>
            )}
          </div>
          <div className="md:w-96">
            <CPSearch />
          </div>
        </div>
      </section>

      {/* Search bar */}
      <section className="max-w-7xl mx-auto px-6 pt-4">
        <div className="relative" data-testid="directory-search-section">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <Search className="w-5 h-5 text-zinc-400" />
          </div>
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Buscar producto, servicio, comercio o promoción (ej. tacos, 2x1, estética…)"
            className="w-full pl-12 pr-12 py-4 rounded-2xl border border-border bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/40 focus:border-[#7C3AED] text-sm md:text-base"
            data-testid="directory-search-input"
          />
          {qInput && (
            <button
              type="button"
              onClick={() => setQInput("")}
              className="absolute inset-y-0 right-4 flex items-center text-zinc-400 hover:text-zinc-700"
              data-testid="directory-search-clear"
              aria-label="Limpiar búsqueda"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </section>

      {/* Giro chips */}
      {giros.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-4" data-testid="giro-filter-section">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mr-1">
              <Filter className="w-3.5 h-3.5" /> Filtrar por giro:
            </div>
            <button
              onClick={() => setGiro("")}
              className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${!giro ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-white border-border hover:border-[#C4B5FD] hover:bg-[#F5F3FF] text-zinc-700"}`}
              data-testid="giro-chip-all"
            >
              Todos
            </button>
            {giros.map((g) => {
              const active = giro.toLowerCase() === g.toLowerCase();
              return (
                <button
                  key={g}
                  onClick={() => setGiro(active ? "" : g)}
                  className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${active ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-white border-border hover:border-[#C4B5FD] hover:bg-[#F5F3FF] text-zinc-700"}`}
                  data-testid={`giro-chip-${g.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {g}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Sort indicator */}
      {!loading && results.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground bg-white border border-border rounded-full px-3 py-1" data-testid="sort-indicator">
            <TrendingUp className="w-3 h-3 text-emerald-600" />
            Ordenados por mejor promoción primero · {results.length} comercio{results.length === 1 ? "" : "s"}
          </div>
        </section>
      )}

      {/* Results grid */}
      <section className="max-w-7xl mx-auto px-6 pt-4 pb-16">
        {loading ? (
          <div className="text-muted-foreground" data-testid="directory-loading">Cargando comercios…</div>
        ) : results.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 stagger" data-testid="directory-results">
            {results.map((b, i) => (
              <AdCard key={b.id} business={b} ad={b.ad} index={i} />
            ))}
          </div>
        ) : hasFilters ? (
          <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="directory-empty-filter">
            <div className="font-display text-2xl">Sin resultados</div>
            <p className="text-muted-foreground mt-2">
              {q && <>No encontramos comercios que coincidan con "<b>{q}</b>"{giro ? <> en el giro <b>{giro}</b></> : ""}.</>}
              {!q && giro && <>No hay comercios en el giro "<b>{giro}</b>" en esta zona.</>}
            </p>
            <div className="flex gap-2 justify-center mt-5">
              {q && (
                <button onClick={() => setQInput("")} className="rounded-full px-5 py-2.5 border border-border bg-white hover:bg-muted text-sm" data-testid="clear-q-btn">
                  Limpiar búsqueda
                </button>
              )}
              {giro && (
                <button onClick={() => setGiro("")} className="btn-primary inline-block rounded-full px-5 py-2.5 text-sm" data-testid="clear-giro-btn">
                  Ver todos los giros
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-2xl p-12 text-center" data-testid="directory-empty">
            <div className="font-display text-2xl">Sin comercios todavía en esta zona</div>
            <p className="text-muted-foreground mt-2">¿Tienes un negocio aquí? Sé el primero en aparecer en este directorio.</p>
            <Link to="/registro" className="btn-primary inline-block mt-5 rounded-full px-6 py-3 font-medium">
              Registrar mi comercio
            </Link>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
